// src/components/DelegationDashboard.jsx
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../hooks/useWeb3';
import { 
  fetchGovernanceInfo, 
  fetchActiveProposals,
  exposeContractsToWindow,
  getUserVotingHistory,
  fetchYILDXTokenBalance,
} from '../services/web3Service';

// Import functions from delegationService
import {
  // On-chain delegator profile functions
  fetchAllRegisteredDelegators,
  fetchDelegatorsAcceptingDelegations,
  fetchDelegatorProfileFromContract,
  isRegisteredDelegator,
  updateDelegatorProfile,
  removeDelegatorProfile,
  
  // Delegation functions
  fetchDelegators,
  delegateTokens,
  removeDelegation,
  getDelegationInfo,
  getVotingPower,
  getDelegatedVotingPower,
  getDelegationStatistics,
  
  // Existing functions
  storeDelegationHistory,
  calculateApprovalRate,
  verifyOperatorStatus,
} from '../services/delegationService';

// Import sub-components
import { 
  DelegatorsList, 
  DelegatorProfileForm, 
  DelegationConfirmationModal,
  UserInfoPanel,
  DelegationStatsSection,
  DelegatorsToMeSection
} from './DelegationComponents';

// Hardcoded operator address (you should replace this with your actual operator address)
const OPERATOR_ADDRESS = '0x15d34AAf54267DB7D7c367839AAf71A00a2C6A65';

const DelegationDashboard = () => {
  // Use the web3 hook to connect to blockchain
  const web3 = useWeb3();
  const { 
    provider, signer, account, contract,
    governanceContract, yildxTokenContract,
    isLoading: web3Loading, error: web3Error, 
    connect 
  } = web3;
  
  // State management
  const [tokenBalance, setTokenBalance] = useState(0);
  const [currentDelegate, setCurrentDelegate] = useState(null);
  const [delegators, setDelegators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDelegator, setSelectedDelegator] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [activeProposals, setActiveProposals] = useState(0);
  const [governanceInfo, setGovernanceInfo] = useState({
    yildxBalance: '0',
    pendingFees: '0',
    votingPower: 0,
    hasGovernanceAccess: false
  });
  const [userDelegators, setUserDelegators] = useState([]);
  const [delegatedVotingPower, setDelegatedVotingPower] = useState('0');
  const [votingHistory, setVotingHistory] = useState([]);
  
  // State for becoming a delegator
  const [showDelegatorForm, setShowDelegatorForm] = useState(false);
  const [editingDelegatorProfile, setEditingDelegatorProfile] = useState(false);
  const [delegatorProfile, setDelegatorProfile] = useState({
    name: '',
    description: '',
    acceptingDelegations: true
  });
  const [operatorAddress, setOperatorAddress] = useState(OPERATOR_ADDRESS);
  const [operatorInfo, setOperatorInfo] = useState(null);
  const [fundContract, setFundContract] = useState(null);
  
  // New state for delegation statistics
  const [delegationStats, setDelegationStats] = useState([]);
  const [showDelegationStats, setShowDelegationStats] = useState(false);
  const [loadingDelegators, setLoadingDelegators] = useState(false);

  // New state for user's delegator status
  const [isUserDelegator, setIsUserDelegator] = useState(false);
  const [delegationStatistics, setDelegationStatistics] = useState({
    totalDelegations: 0,
    totalDelegators: 0,
    totalDelegatees: 0,
    delegationsByDelegatee: []
  });
  
  // New state for fund operators
  const [fundOperators, setFundOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(true);
  const [showOperators, setShowOperators] = useState(false);
  
  // Determine if connected to blockchain
  const isConnected = !!account;
  
  // Initialize fund contract on provider change
  useEffect(() => {
    const initFundContract = async () => {
      if (provider && contract) {
        try {
          console.log("Using contract from web3 hook");
          setFundContract(contract);
        } catch (error) {
          console.error("Error initializing fund contract:", error);
        }
      }
    };
    
    initFundContract();
  }, [provider, contract]);
  
  // Fetch fund operators
  const fetchOperators = async () => {
    if (!fundContract) return;
    
    try {
      setLoadingOperators(true);
      
      // Array to store found operators
      const foundOperators = [];
      
      // Get owner of the fund for comparison
      let owner = "";
      try {
        owner = await fundContract.owner();
      } catch (err) {
        console.warn("Could not get fund owner:", err.message);
      }
      
      // Method 1: Get operators through events
      try {
        // Get OperatorAdded events
        const addFilter = fundContract.filters.OperatorAdded();
        const addEvents = await fundContract.queryFilter(addFilter);
        
        // Get OperatorRemoved events
        const removeFilter = fundContract.filters.OperatorRemoved();
        const removeEvents = await fundContract.queryFilter(removeFilter);
        
        console.log(`Found ${addEvents.length} OperatorAdded events and ${removeEvents.length} OperatorRemoved events`);
        
        // Process events to find current operators
        const operatorMap = {};
        
        // Process add events
        for (const event of addEvents) {
          const operator = event.args.operator;
          operatorMap[operator.toLowerCase()] = {
            address: operator,
            added: true,
            blockNumber: event.blockNumber
          };
        }
        
        // Process remove events
        for (const event of removeEvents) {
          const address = event.args.operator.toLowerCase();
          // Only mark as removed if the remove event is after the add event
          if (operatorMap[address] && event.blockNumber > operatorMap[address].blockNumber) {
            operatorMap[address].added = false;
          }
        }
        
        // Verify current status for each potential operator
        for (const [address, data] of Object.entries(operatorMap)) {
          if (data.added) {
            try {
              const isStillOperator = await fundContract.authorizedOperators(data.address);
              
              if (isStillOperator) {
                // Get token balance if yildxTokenContract is available
                let tokenBalance = "0";
                if (yildxTokenContract) {
                  try {
                    tokenBalance = await yildxTokenContract.balanceOf(data.address);
                    tokenBalance = ethers.utils.formatEther(tokenBalance);
                  } catch (err) {
                    console.warn(`Could not get token balance for operator ${data.address}:`, err.message);
                  }
                }
                
                foundOperators.push({
                  address: data.address,
                  isOwner: data.address.toLowerCase() === owner.toLowerCase(),
                  tokenBalance,
                  isCurrentUser: data.address.toLowerCase() === account?.toLowerCase()
                });
              }
            } catch (err) {
              console.warn(`Error checking operator status for ${data.address}:`, err);
            }
          }
        }
      } catch (err) {
        console.warn("Could not fetch operator events:", err);
      }
      
      // Method 2: Direct check of hardcoded operator address
      if (foundOperators.length === 0 && operatorAddress) {
        try {
          const isOperator = await verifyOperatorStatus(fundContract, operatorAddress);
          
          if (isOperator) {
            // Get token balance if yildxTokenContract is available
            let tokenBalance = "0";
            if (yildxTokenContract) {
              try {
                tokenBalance = await yildxTokenContract.balanceOf(operatorAddress);
                tokenBalance = ethers.utils.formatEther(tokenBalance);
              } catch (err) {
                console.warn(`Could not get token balance for operator ${operatorAddress}:`, err.message);
              }
            }
            
            foundOperators.push({
              address: operatorAddress,
              isOwner: operatorAddress.toLowerCase() === owner.toLowerCase(),
              tokenBalance,
              isCurrentUser: operatorAddress.toLowerCase() === account?.toLowerCase()
            });
          }
        } catch (err) {
          console.warn(`Error checking operator status for ${operatorAddress}:`, err);
        }
      }
      
      // Method 3: Check common addresses
      if (foundOperators.length === 0) {
        const commonAddresses = [
          "0x70997970C51812dc3A010C7d01b50e0d17dc79C8", // Hardhat default account
          "0x413b1AfCa96a3df5A686d8BFBF93d30688a7f7D9"  // From previous output
        ];
        
        for (const address of commonAddresses) {
          try {
            const isOperator = await fundContract.authorizedOperators(address);
            
            if (isOperator) {
              foundOperators.push({
                address,
                isOwner: address.toLowerCase() === owner.toLowerCase(),
                tokenBalance: "0",
                isCurrentUser: address.toLowerCase() === account?.toLowerCase()
              });
            }
          } catch (err) {
            console.warn(`Error checking operator status for ${address}:`, err);
          }
        }
      }
      
      setFundOperators(foundOperators);
    } catch (err) {
      console.error("Error fetching operators:", err);
    } finally {
      setLoadingOperators(false);
    }
  };
  
  // Check if user is delegating or has delegators
  const checkDelegationStatus = async () => {
    try {
      if (!governanceContract || !account) return;
      
      setLoadingDelegators(true);
      
      // Check if user is delegating to anyone
      try {
        const delegationInfo = await getDelegationInfo(governanceContract, account);
        if (delegationInfo.hasDelegated) {
          const delegateeProfile = await fetchDelegatorProfileFromContract(governanceContract, delegationInfo.delegatee);
          
          // If profile doesn't exist on-chain, fetch basic info
          if (!delegateeProfile) {
            // Fetch token balance for the delegatee
            const balance = await fetchYILDXTokenBalance(yildxTokenContract, delegationInfo.delegatee);
            
            // Check if this is the operator
            const isOperator = delegationInfo.delegatee.toLowerCase() === operatorAddress.toLowerCase();
            
            // Create a basic delegatee object
            const basicDelegatee = {
              address: delegationInfo.delegatee,
              tokens: parseFloat(balance),
              description: isOperator ? 'Network Operator' : 'Governance participant',
              name: '',
              acceptingDelegations: true,
              isOperator: isOperator,
              votingHistory: 0,
              approvalRate: null
            };
            
            setCurrentDelegate(basicDelegatee);
          } else {
            // Use the fetched on-chain profile
            const balance = await fetchYILDXTokenBalance(yildxTokenContract, delegationInfo.delegatee);
            const isOperator = delegationInfo.delegatee.toLowerCase() === operatorAddress.toLowerCase();
            
            setCurrentDelegate({
              address: delegationInfo.delegatee,
              tokens: parseFloat(balance),
              description: delegateeProfile.description || 'Governance participant',
              name: delegateeProfile.name || '',
              acceptingDelegations: delegateeProfile.acceptingDelegations,
              isOperator: isOperator,
              votingHistory: delegateeProfile.votingHistoryCount || 0,
              approvalRate: delegateeProfile.approvalRate || null
            });
          }
        } else {
          setCurrentDelegate(null);
        }
      } catch (error) {
        console.error("Error checking delegation status:", error);
        setCurrentDelegate(null);
      }
      
      // Check if user has any delegators (accounts delegating to this user)
      try {
        const userDelegatorsArr = await fetchDelegators(governanceContract, account);
        
        if (userDelegatorsArr && userDelegatorsArr.length > 0) {
          setUserDelegators(userDelegatorsArr);
          
          // Get delegated voting power directly from contract
          try {
            const delegatedPower = await getDelegatedVotingPower(governanceContract, account);
            setDelegatedVotingPower(delegatedPower);
          } catch (powerErr) {
            console.error("Error calculating delegated voting power:", powerErr);
            setDelegatedVotingPower('0');
          }
        } else {
          setUserDelegators([]);
          setDelegatedVotingPower('0');
        }
      } catch (error) {
        console.error("Error fetching delegators:", error);
        setUserDelegators([]);
        setDelegatedVotingPower('0');
      } finally {
        setLoadingDelegators(false);
      }
    } catch (error) {
      console.error("Error in checkDelegationStatus:", error);
      setLoadingDelegators(false);
    }
  };
  
  // Fetch all delegation statistics
  const fetchDelegationStatistics = async () => {
    if (!governanceContract || !yildxTokenContract) return;
    
    try {
      setLoading(true);
      console.log("Fetching delegation statistics from contract...");
      
      // Get comprehensive delegation statistics
      const stats = await getDelegationStatistics(governanceContract, yildxTokenContract);
      setDelegationStatistics(stats);
      
      // Set the delegationStats array for backwards compatibility
      setDelegationStats(stats.delegationsByDelegatee);
      
    } catch (error) {
      console.error("Error fetching delegation statistics:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Load data when component mounts or when account/contract changes
  useEffect(() => {
    const loadData = async () => {
      if (isConnected && yildxTokenContract && governanceContract) {
        try {
          setLoading(true);
          
          console.log("Loading on-chain delegation data...");
          
          // Get governance info for the current user
          const govInfo = await fetchGovernanceInfo(yildxTokenContract, governanceContract, account);
          setGovernanceInfo(govInfo);
          setTokenBalance(parseFloat(govInfo.yildxBalance));
          
          // Expose contracts to window for debugging if in development mode
          if (process.env.NODE_ENV === 'development') {
            exposeContractsToWindow({
              provider, signer, account, 
              fundContract, 
              governanceContract, 
              yildxTokenContract
            });
          }
          
          // Check delegation status (if user is delegating or has delegators)
          await checkDelegationStatus();
          
          // Get active proposals count
          try {
            const proposals = await fetchActiveProposals(governanceContract);
            const activeCount = proposals.filter(p => !p.executed && !p.canceled && (p.votingEndTime * 1000) > Date.now()).length;
            setActiveProposals(activeCount);
          } catch (error) {
            console.error("Error getting proposal count:", error);
          }
          
          // Get voting history for the current user
          try {
            const history = await getUserVotingHistory(governanceContract, account);
            setVotingHistory(history);
          } catch (error) {
            console.error("Error getting voting history:", error);
          }
          
          // Check if current user is a registered delegator
          try {
            const isRegistered = await isRegisteredDelegator(governanceContract, account);
            setIsUserDelegator(isRegistered);
            
            // If registered, get the profile
            if (isRegistered) {
              const profile = await fetchDelegatorProfileFromContract(governanceContract, account);
              if (profile) {
                setDelegatorProfile({
                  name: profile.name,
                  description: profile.description,
                  acceptingDelegations: profile.acceptingDelegations
                });
              }
            }
          } catch (error) {
            console.error("Error checking delegator status:", error);
            setIsUserDelegator(false);
          }
          
          // Get operator info
          await fetchOperatorInfo();
          
          // Get all registered delegators
          try {
            const allDelegators = await fetchDelegatorsAcceptingDelegations(governanceContract);
            
            // Map the result to our delegator format
            const mappedDelegators = await Promise.all(
              allDelegators.addresses.map(async (address, index) => {
                // Get token balance for this address
                let formattedBalance = 0;
                try {
                  const balance = await fetchYILDXTokenBalance(yildxTokenContract, address);
                  formattedBalance = parseFloat(balance);
                } catch (err) {
                  console.log(`Could not get token balance for ${address}:`, err.message);
                }
                
                // Check if this is the operator
                const isOperator = address.toLowerCase() === operatorAddress.toLowerCase();
                
                // Check if this is the current user
                const isCurrentUser = address.toLowerCase() === account.toLowerCase();
                
                // Get the delegators for this address to count them
                let delegatorsCount = 0;
                let delegatedPower = "0";
                try {
                  const addressDelegators = await fetchDelegators(governanceContract, address);
                  delegatorsCount = addressDelegators.length;
                  
                  // Get delegated power
                  delegatedPower = await getDelegatedVotingPower(governanceContract, address);
                } catch (err) {
                  console.log(`Could not get delegators for ${address}:`, err.message);
                }
                
                return {
                  address: address,
                  tokens: formattedBalance,
                  description: allDelegators.descriptions[index] || 'YILDX token holder',
                  name: allDelegators.names[index] || '',
                  votingHistory: allDelegators.votingHistoryCounts[index].toNumber(),
                  approvalRate: allDelegators.approvalRates[index].toNumber(), 
                  acceptingDelegations: true, // They're in the accepting list
                  isOperator: isOperator,
                  isCurrentUser: isCurrentUser,
                  delegatorsCount: delegatorsCount,
                  delegatedPower: delegatedPower
                };
              })
            );
            
            // Add operator to the list if not already included
            if (operatorInfo && !mappedDelegators.some(d => d.address.toLowerCase() === operatorInfo.address.toLowerCase())) {
              operatorInfo.isCurrentUser = operatorInfo.address.toLowerCase() === account.toLowerCase();
              mappedDelegators.push(operatorInfo);
            }
            
            setDelegators(mappedDelegators);
          } catch (error) {
            console.error("Error getting delegators:", error);
          }
          
          // Fetch delegation statistics
          await fetchDelegationStatistics();
          
          // Fetch fund operators
          await fetchOperators();
          
        } catch (error) {
          console.error("Error loading delegation data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    
    if (isConnected) {
      loadData();
    }
  }, [isConnected, account, yildxTokenContract, governanceContract, provider, signer, fundContract, operatorAddress]);

  // Function to check operator status and get operator info
  const fetchOperatorInfo = async () => {
    if (!operatorAddress || !yildxTokenContract || !governanceContract) {
      console.log("Missing dependencies for operator info");
      return;
    }
    
    try {
      // Get operator token balance
      const balance = await fetchYILDXTokenBalance(yildxTokenContract, operatorAddress);
      const formattedBalance = parseFloat(balance);
      
      // Check if operator is a registered delegator
      const isRegistered = await isRegisteredDelegator(governanceContract, operatorAddress);
      let operatorProfile = null;
      
      if (isRegistered) {
        operatorProfile = await fetchDelegatorProfileFromContract(governanceContract, operatorAddress);
      }
      
      // Verify this is the operator by calling the fund contract
      const verifiedOperator = await verifyOperatorStatus(fundContract, operatorAddress);
      
      if (verifiedOperator) {
        // Get voting history for the operator
        let votingHistoryArr = [];
        try {
          votingHistoryArr = await getUserVotingHistory(governanceContract, operatorAddress);
        } catch (err) {
          console.log("Could not get operator voting history:", err.message);
        }
        
        // Get voting history count
        const votingHistoryCount = operatorProfile?.votingHistoryCount || votingHistoryArr.length || 0;
        
        // Calculate approval rate if voting history exists
        let approvalRate = operatorProfile?.approvalRate || null;
        if (!approvalRate && votingHistoryArr.length > 0) {
          try {
            approvalRate = calculateApprovalRate(votingHistoryArr);
          } catch (err) {
            console.log("Could not calculate operator approval rate:", err.message);
          }
        }
        
        const operatorData = {
          address: operatorAddress,
          tokens: formattedBalance,
          description: operatorProfile?.description || 'Network Operator responsible for fund maintenance',
          name: operatorProfile?.name || 'YieldX Operator',
          votingHistory: votingHistoryCount,
          approvalRate: approvalRate,
          acceptingDelegations: operatorProfile?.acceptingDelegations || true,
          isOperator: true
        };
        
        console.log("Setting operator info:", operatorData);
        setOperatorInfo(operatorData);
      } else {
        console.log(`Address ${operatorAddress} is not verified as an operator`);
      }
    } catch (error) {
      console.error("Error fetching operator info:", error);
    }
  };

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Format token amounts
  const formatTokens = (amount) => {
    return new Intl.NumberFormat().format(parseFloat(amount) || 0);
  };

  // Handle delegation
  const handleDelegate = (delegator) => {
    setSelectedDelegator(delegator);
    setShowConfirmation(true);
  };

  // Toggle accepting delegations
  const toggleAcceptingDelegations = async () => {
    if (!account || !governanceContract) {
      alert("Please connect your wallet first");
      return;
    }
    
    try {
      setLoading(true);
      
      // Update profile on-chain
      const success = await updateDelegatorProfile(
        governanceContract,
        delegatorProfile.name,
        delegatorProfile.description,
        !delegatorProfile.acceptingDelegations
      );
      
      if (success) {
        // Update local state
        setDelegatorProfile(prev => ({
          ...prev,
          acceptingDelegations: !prev.acceptingDelegations
        }));
        
        // Update delegators list
        setDelegators(prevDelegators => {
          return prevDelegators.map(d => {
            if (d.address.toLowerCase() === account.toLowerCase()) {
              return {
                ...d,
                acceptingDelegations: !delegatorProfile.acceptingDelegations
              };
            }
            return d;
          });
        });
        
        alert(`You are now ${!delegatorProfile.acceptingDelegations ? 'accepting' : 'not accepting'} delegations.`);
        
        // Refresh delegation statistics
        await fetchDelegationStatistics();
      } else {
        alert("Failed to update delegation acceptance status. Please try again.");
      }
    } catch (error) {
      console.error("Error toggling delegation acceptance:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Confirm delegation
  const confirmDelegation = async () => {
    if (!governanceContract) {
      alert("Cannot connect to governance contract. Please try again.");
      return;
    }
    
    try {
      setLoading(true);
      // Delegate tokens directly from contract
      const success = await delegateTokens(governanceContract, selectedDelegator.address);
      
      if (success) {
        // Update state after successful transaction
        setCurrentDelegate(selectedDelegator);
        setShowConfirmation(false);
        
        // Store delegation history in local storage for backward compatibility
        storeDelegationHistory(account, selectedDelegator.address, 'delegate');
        
        alert(`Successfully delegated to ${selectedDelegator.name || formatAddress(selectedDelegator.address)}`);
        
        // Refresh delegation statistics and status
        await checkDelegationStatus();
        await fetchDelegationStatistics();
      } else {
        alert("Delegation transaction failed. Please try again.");
      }
    } catch (error) {
      console.error("Error delegating tokens:", error);
      alert(`Error delegating tokens: ${error.message}`);
      setShowConfirmation(false);
    } finally {
      setLoading(false);
    }
  };

  // Remove delegation
  const handleRemoveDelegation = async () => {
    if (!governanceContract) {
      alert("Cannot connect to governance contract. Please try again.");
      return;
    }
    
    try {
      setLoading(true);
      // Save address before removing for history
      const delegateeAddress = currentDelegate.address;
      
      // Remove delegation directly from contract
      const success = await removeDelegation(governanceContract);
      
      if (success) {
        // Update state after successful transaction
        setCurrentDelegate(null);
        
        // Store delegation removal in history for backward compatibility
        storeDelegationHistory(account, delegateeAddress, 'undelegate');
        
        alert('Delegation successfully removed');
        
        // Refresh delegation statistics and status
        await checkDelegationStatus();
        await fetchDelegationStatistics();
      } else {
        alert("Delegation removal failed. Please try again.");
      }
    } catch (error) {
      console.error("Error removing delegation:", error);
      alert(`Error removing delegation: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Start editing user profile
  const startEditingProfile = (delegator) => {
    setEditingDelegatorProfile(true);
    setDelegatorProfile({
      name: delegator.name || '',
      description: delegator.description || '',
      acceptingDelegations: delegator.acceptingDelegations
    });
  };
  
  // Submit delegator application or update profile
  const submitDelegatorProfile = async () => {
    if (!account || !governanceContract) {
      alert("Please connect your wallet first");
      return;
    }
    
    if (!delegatorProfile.name.trim()) {
      alert("Please provide a name for your delegator profile");
      return;
    }
    
    if (!delegatorProfile.description.trim()) {
      alert("Please provide a description for your delegator profile");
      return;
    }
    
    try {
      setLoading(true);
      
      // Update profile on-chain
      const success = await updateDelegatorProfile(
        governanceContract,
        delegatorProfile.name,
        delegatorProfile.description,
        delegatorProfile.acceptingDelegations
      );
      
      if (success) {
        alert(isUserDelegator 
          ? "Your delegator profile has been updated." 
          : "Your delegator profile has been saved. You are now listed as an available delegator.");
        
        setShowDelegatorForm(false);
        setEditingDelegatorProfile(false);
        setIsUserDelegator(true);
        
        // Refresh data
        await checkDelegationStatus();
        await fetchDelegationStatistics();
      } else {
        alert("There was an error saving your delegator profile. Please try again.");
      }
    } catch (error) {
      console.error("Error saving delegator profile:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle input changes for delegator profile
  const handleProfileChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDelegatorProfile(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Filter delegators based on search term
  const filteredDelegators = delegators.filter(delegator => 
    (delegator.address && delegator.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (delegator.description && delegator.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (delegator.name && delegator.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Check if the current user is an operator
  const isUserOperator = fundOperators.some(op => op.isCurrentUser);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">YILDX Token Delegation Dashboard</h2>
      
      <main className="container mx-auto">
        {isConnected ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left sidebar - User info */}
            <UserInfoPanel 
              account={account}
              tokenBalance={tokenBalance}
              currentDelegate={currentDelegate}
              governanceInfo={governanceInfo}
              delegatedVotingPower={delegatedVotingPower}
              userDelegators={userDelegators}
              votingHistory={votingHistory}
              isUserDelegator={isUserDelegator}
              delegatorProfile={delegatorProfile}
              operatorInfo={operatorInfo}
              activeProposals={activeProposals}
              loading={loading}
              onEditProfile={startEditingProfile}
              onToggleAcceptingDelegations={toggleAcceptingDelegations}
              onRemoveDelegation={handleRemoveDelegation}
              formatAddress={formatAddress}
              formatTokens={formatTokens}
            />
            
            {/* Main content - Delegators list */}
            <div className="col-span-2">
              {/* Display operator notice for users with operator privileges */}
              {isUserOperator && (
                <div className="bg-indigo-100 border-l-4 border-indigo-500 p-4 mb-6 rounded-r-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-indigo-800">
                        Your account has <span className="font-bold">operator privileges</span> for this fund. 
                        You can perform management actions like rebalancing and trading without governance approval.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h2 className="text-xl font-semibold mb-4">Delegate Your Tokens</h2>
                <p className="text-gray-600 mb-4">
                  Delegate your voting power to active community members who align with your views.
                  You retain full ownership of your tokens while empowering others to vote on your behalf.
                </p>
                
                {/* Fund Operators Section */}
                <div className="mb-6 border rounded-lg overflow-hidden">
                  <div className="bg-indigo-50 p-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-indigo-900">Fund Operators</h3>
                      <button 
                        onClick={() => setShowOperators(!showOperators)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        {showOperators ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded operators content */}
                  {showOperators && (
                    <div className="bg-white p-4">
                      {loadingOperators ? (
                        <div className="flex justify-center items-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-500"></div>
                          <span className="ml-2 text-gray-600">Loading operators...</span>
                        </div>
                      ) : fundOperators.length > 0 ? (
                        <>
                          <div className="space-y-3">
                            <p className="text-sm text-gray-600 mb-2">
                              The following addresses are authorized to perform management actions on the fund:
                            </p>
                            
                            {fundOperators.map((operator, index) => (
                              <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium">{operator.address}</p>
                                    <p className="text-sm text-gray-500">{formatAddress(operator.address)}</p>
                                  </div>
                                  <div className="flex space-x-2">
                                    <span className="inline-block bg-indigo-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded">
                                      Operator
                                    </span>
                                    {operator.isOwner && (
                                      <span className="inline-block bg-purple-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded">
                                        Owner
                                      </span>
                                    )}
                                    {operator.isCurrentUser && (
                                      <span className="inline-block bg-green-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded">
                                        You
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {parseFloat(operator.tokenBalance) > 0 && (
                                  <div className="mt-2 text-sm text-gray-600">
                                    <span className="font-medium">Balance:</span> {formatTokens(operator.tokenBalance)} YILDX
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          <div className="mt-4 bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
                            <h4 className="font-medium mb-1">About Fund Operators</h4>
                            <p>
    Operators have special privileges to manage the fund, but only within the boundaries established by governance voting. 
    They can perform day-to-day operations such as rebalancing the portfolio according to approved allocations, 
    distributing ETH, and executing trades that follow the fund's set parameters. This balance allows for efficient 
    management while ensuring all activities adhere to the rules and allocations democratically approved by token holders.
                            </p>
                          </div>
                        </>
                      ) : (
                        <p className="text-gray-600 text-sm">No operators found for this fund.</p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Search bar */}
                <div className="mb-6">
                  <input
                    type="text"
                    placeholder="Search delegators by address, name, or description..."
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                {/* Delegators list */}
                <DelegatorsList 
                  delegators={filteredDelegators}
                  currentDelegate={currentDelegate}
                  delegatorProfile={delegatorProfile}
                  loading={loading}
                  account={account}
                  onDelegate={handleDelegate}
                  onEditProfile={startEditingProfile}
                  onToggleAcceptingDelegations={toggleAcceptingDelegations}
                  onRemoveDelegation={handleRemoveDelegation}
                  formatAddress={formatAddress}
                  formatTokens={formatTokens}
                />
                
                {/* Delegation Statistics Section */}
                <DelegationStatsSection 
                  showDelegationStats={showDelegationStats}
                  delegationStatistics={delegationStatistics}
                  loading={loading}
                  operatorAddress={operatorAddress}
                  account={account}
                  onToggleStats={() => {
                    setShowDelegationStats(!showDelegationStats);
                    if (!showDelegationStats) fetchDelegationStatistics();
                  }}
                  formatAddress={formatAddress}
                  formatTokens={formatTokens}
                />
                
                {/* Become a delegator info */}
                {!isUserDelegator && !editingDelegatorProfile && (
                  <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium">Want to become a delegator?</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Active governance participants with at least 100 YILDX tokens can volunteer
                      to be listed as potential delegators.
                    </p>
                    <button 
                      onClick={() => setShowDelegatorForm(!showDelegatorForm)}
                      className="mt-3 text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      {showDelegatorForm ? 'Cancel application' : 'Apply to become a delegator'}
                    </button>
                    
                    {/* Delegator application form */}
                    {showDelegatorForm && (
                      <DelegatorProfileForm 
                        delegatorProfile={delegatorProfile}
                        loading={loading}
                        onProfileChange={handleProfileChange}
                        onSubmit={submitDelegatorProfile}
                        isEditing={false}
                        onCancel={() => setShowDelegatorForm(false)}
                      />
                    )}
                  </div>
                )}
                
                {/* Edit profile form */}
                {editingDelegatorProfile && (
                  <DelegatorProfileForm 
                    delegatorProfile={delegatorProfile}
                    loading={loading}
                    onProfileChange={handleProfileChange}
                    onSubmit={submitDelegatorProfile}
                    isEditing={true}
                    onCancel={() => setEditingDelegatorProfile(false)}
                  />
                )}
                
                {/* On-chain storage notice */}
                <div className="mt-8 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                  <h4 className="font-medium text-green-800">About Delegation Profiles</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Delegator profiles are now stored on-chain with the following benefits:
                  </p>
                  <ul className="list-disc list-inside text-sm text-green-700 mt-2">
                    <li>Your profile is visible to all users across all browsers</li>
                    <li>Profile data is securely stored on the blockchain</li>
                    <li>Profiles and delegation data are consistently available on all devices</li>
                  </ul>
                  <p className="text-sm text-green-700 mt-2">
                    All delegation actions (creating profiles, delegating tokens, etc.) will require blockchain transactions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Not connected state
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Welcome to YILDX Governance</h2>
            <p className="max-w-md mx-auto mb-8 text-gray-600">
              Connect your wallet to view your delegation status, delegate your tokens,
              or participate in governance.
            </p>
            <button 
              onClick={connect}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg text-lg"
              disabled={web3Loading}
            >
              {web3Loading ? 'Connecting...' : 'Connect Wallet'}
            </button>
            
            {web3Error && (
              <p className="text-red-500 mt-4">{web3Error}</p>
            )}
          </div>
        )}
      </main>
      
      {/* Delegation confirmation modal */}
      <DelegationConfirmationModal 
        showConfirmation={showConfirmation}
        selectedDelegator={selectedDelegator}
        loading={loading}
        onConfirm={confirmDelegation}
        onCancel={() => setShowConfirmation(false)}
        formatAddress={formatAddress}
        formatTokens={formatTokens}
      />
    </div>
  );
};

export default DelegationDashboard;